import Cookies from "js-cookie"

/**
 * Centralized authentication service for managing tokens and auth state
 */
class AuthService {
  private getCompanyIndex(): string {
    if (typeof window !== "undefined") {
      const match = window.location.pathname.match(/^\/c\/([^/]+)/)
      return match ? match[1] : "0"
    }
    return "0"
  }

  private getCookieConfig(): Cookies.CookieAttributes {
    if (process.env.NODE_ENV === "development") {
      return {
        path: "/",
        sameSite: "lax",
        secure: false,
      }
    }

    return {
      secure: true,
      sameSite: "lax",
      path: "/",
      domain: process.env.NEXT_PUBLIC_DOMAIN_COOKIE,
    }
  }

  /**
   * Get the current access token from cookies.
   * Reads from indexed sso-token-esign-{index} first, falls back to legacy sso-token.
   */
  getToken(): string | undefined {
    const index = this.getCompanyIndex()
    return Cookies.get(`sso-token-esign-${index}`) || Cookies.get("sso-token")
  }

  /**
   * Set the access token in cookies
   */
  setToken(token: string, index?: string): void {
    const targetIndex = index ?? this.getCompanyIndex()
    Cookies.set(`sso-token-esign-${targetIndex}`, token, this.getCookieConfig())
  }

  /**
   * Set the access token and refresh token in cookies
   */
  setTokens(token: string, refreshToken?: string, index?: string): void {
    const targetIndex = index ?? this.getCompanyIndex()
    Cookies.set(`sso-token-esign-${targetIndex}`, token, this.getCookieConfig())
    if (refreshToken) {
      Cookies.set(`sso-refresh-token-esign-${targetIndex}`, refreshToken, this.getCookieConfig())
    }
  }

  /**
   * Maps an index to a companyId in the shared company-index-map cookie
   */
  setCompanyIndexMap(index: string, companyId: string): void {
    const existingCookie = Cookies.get("company-index-map")
    let map: Record<string, string> = {}
    try {
      if (existingCookie) {
        map = JSON.parse(existingCookie)
      }
    } catch {
      // ignore parse error
    }
    map[index] = companyId
    Cookies.set("company-index-map", JSON.stringify(map), this.getCookieConfig())
  }

  /**
   * Clear all authentication tokens
   */
  clearTokens(): void {
    const index = this.getCompanyIndex()
    Cookies.remove(`sso-token-esign-${index}`, this.getCookieConfig())
    Cookies.remove(`sso-refresh-token-esign-${index}`, this.getCookieConfig())
    Cookies.remove("company-index-map", this.getCookieConfig())
    // also remove legacy cookie
    Cookies.remove("sso-token")
    this.clearTempToken()
  }

  /**
   * Set a temporary token used during select-company flow
   */
  setTempToken(token: string): void {
    Cookies.set("sso-token-esign-temp", token, this.getCookieConfig())
  }

  /**
   * Get the temporary token (set after login, before company selection)
   */
  getTempToken(): string | undefined {
    return Cookies.get("sso-token-esign-temp")
  }

  /**
   * Remove the temporary token (called after company is selected)
   */
  clearTempToken(): void {
    Cookies.remove("sso-token-esign-temp", this.getCookieConfig())
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!(this.getToken() || this.getTempToken())
  }

  /**
   * Logout user and redirect to login page
   */
  async logout(apiCall?: () => Promise<void>): Promise<void> {
    try {
      if (apiCall) {
        await apiCall()
      }
    } catch (error) {
      console.error("Logout API call failed:", error)
    } finally {
      this.clearTokens()

      if (typeof window !== "undefined") {
        const currentPath = window.location.pathname + window.location.search
        const loginUrl = new URL("/auth/login", window.location.origin)

        if (
          !currentPath.startsWith("/auth/login") &&
          !currentPath.startsWith("/auth/forgot-password") &&
          !currentPath.startsWith("/auth/reset-password")
        ) {
          loginUrl.searchParams.set("redirect", currentPath)
        }

        if (!currentPath.startsWith("/auth/login")) {
          window.location.href = loginUrl.toString()
        }
      }
    }
  }
}

// Export singleton instance
export const authService = new AuthService()
